import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, delay, mergeMap } from 'rxjs/operators';

/** How long after the first screen is up before the other pages start downloading, so the first screen is never slowed by them. */
export const IDLE_PRELOAD_DELAY_MS = 800;
/** The latest the browser may postpone the downloads for, however busy it is. */
export const IDLE_PRELOAD_DEADLINE_MS = 3000;
/** The gap between one priority and the next. */
export const PRELOAD_STEP_MS = 1500;
/** A route that does not say (`data: { preloadPriority }`) goes after the ones that do. Lower numbers go first. */
export const DEFAULT_PRELOAD_PRIORITY = 5;

/** True on a data-saver connection, where downloading pages nobody asked for yet would cost the visitor money. */
const saveDataRequested = (): boolean => {
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
    return connection?.saveData === true;
};

/** Emits once, when the browser is idle (or after the deadline, or at once where there is no idle callback). */
const whenIdle = (): Observable<void> =>
    new Observable<void>((subscriber) => {
        const done = (): void => {
            subscriber.next();
            subscriber.complete();
        };
        const scheduler = globalThis as { requestIdleCallback?: (cb: () => void, options?: { timeout: number }) => number; cancelIdleCallback?: (id: number) => void };
        let idleHandle: number | undefined;
        const timer = setTimeout(() => {
            if (scheduler.requestIdleCallback) {
                idleHandle = scheduler.requestIdleCallback(done, { timeout: IDLE_PRELOAD_DEADLINE_MS });
            } else {
                done();
            }
        }, IDLE_PRELOAD_DELAY_MS);
        return () => {
            clearTimeout(timer);
            if (idleHandle !== undefined) {
                scheduler.cancelIdleCallback?.(idleHandle);
            }
        };
    });

/**
 * Downloads the lazy pages in the background once the first screen is up and the browser is idle.
 *
 * ⚠ WHY THIS EXISTS (owner, 2026-10-27: "Choose Lifemaan... takes too much time to redirect", "sign in takes too much time"). The API answers in
 * about 100 ms; what the visitor waited for was the next page's CODE, fetched as a chain of small files one after the other, each costing a full
 * round trip. Fetching them while the visitor reads the current screen turns that wait into nothing, and the browser keeps them in its cache.
 *
 * ⚠ STAGGERED, MOST LIKELY FIRST. Starting every page at once measured WORSE than nothing for somebody who clicks in the first seconds on a slow
 * line (4.6 s against 2.7 s with no preloading): the background files and the page they actually asked for shared the same narrow connection. So a
 * route starts `preloadPriority` steps after the browser is idle (`data: { preloadPriority }`, default 5), the next screen of the main journey being 0.
 * Each lazy level is a separate decision, so the priority is repeated on a page's own route as well as on the route that leads to it.
 *
 * ⚠ DO NOT "IMPROVE" THIS INTO A QUEUE WITH A LIMIT ON HOW MANY RUN AT ONCE. The router's `load` for a parent route also preloads its children
 * and only finishes when they have, so a limited queue fills with parents that are waiting on children that are waiting in the same queue: it
 * deadlocked after two files, and nothing was preloaded at all.
 *
 * A route opts out with `data: { preload: false }`. Nothing is downloaded on a data-saver connection.
 */
@Injectable({ providedIn: 'root' })
export class IdlePreloadStrategy implements PreloadingStrategy {
    preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
        if (route.data?.['preload'] === false || saveDataRequested()) {
            return of(null);
        }
        const priority = Number(route.data?.['preloadPriority'] ?? DEFAULT_PRELOAD_PRIORITY);
        return whenIdle().pipe(
            delay(priority * PRELOAD_STEP_MS),
            // ⚠ A FAILED DOWNLOAD ENDS HERE (review MR !10 S1). The router merges every route's preload into one stream with no error handler, so one
            // failed file (a flaky line, a hashed file gone after a redeploy) would otherwise stop all preloading for the session and raise an
            // unhandled error - on exactly the slow connections this exists for. Angular's own PreloadAllModules ends the same way.
            mergeMap(() => load().pipe(catchError(() => of(null)))),
        );
    }
}
